import * as yup from 'yup';
import type { ReportPrivateConfigResponse, ReportSaveReceipt } from 'src/components/star-grid/reportPrivateConfig.types';

const text = () => yup.string().nullable().optional();
const labels = yup
  .mixed<Record<string, string>>()
  .nullable()
  .test(
    'labels',
    'Invalid row labels',
    (value) =>
      value == null || (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every((label) => typeof label === 'string'))
  );
const months = () => yup.array().of(yup.number().integer().required()).defined();
export const privateConfigSchema = yup.object({
  deck_type: yup.string().required(),
  deck_report: yup.string().required(),
  row_config: yup
    .array()
    .of(
      yup.object({
        kind: yup.mixed<'header' | 'spacer' | 'value' | 'block' | 'ratio'>().oneOf(['header', 'spacer', 'value', 'block', 'ratio']).required(),
        label: text(),
        element: text(),
        numerator: text(),
        denominator: text(),
        source: text(),
        elements: yup.array().of(yup.string().required()).nullable(),
        labels,
        indent: yup.number().nullable()
      })
    )
    .defined(),
  deck_config: yup
    .object({
      scenarios: yup
        .array()
        .of(
          yup.object({
            reporting_scenario_id: yup.number().integer().required(),
            label: yup.string().required(),
            months: months(),
            forecast_start_period: yup.number().nullable()
          })
        )
        .defined(),
      column_axis: yup
        .array()
        .of(
          yup.object({
            kind: yup.mixed<'period' | 'spacer' | 'variance'>().oneOf(['period', 'spacer', 'variance']).required(),
            label: text(),
            group: text(),
            scenario: text(),
            period: text(),
            base_scenario: text(),
            compare_scenario: text(),
            compare_period: text(),
            exfx: yup.boolean(),
            fx_vintage: text(),
            currency: text()
          })
        )
        .defined(),
      variance_pairs: yup
        .array()
        .of(
          yup.object({
            base_scenario: yup.string().required(),
            compare_scenario: yup.string().required(),
            base_months: months(),
            compare_months: months(),
            include_exfx: yup.boolean(),
            fx_scenario: text(),
            forecast_start_period: yup.number().nullable()
          })
        )
        .defined()
    })
    .required()
});
const receiptSchema = yup.object({
  pnl_report_id: yup.number().integer().positive().required(),
  status: yup.string().required(),
  config_revision: yup.number().integer().positive().required(),
  row_config_ref: yup.string().required(),
  deck_config_ref: yup.string().required(),
  idempotent_replay: yup.boolean(),
  config_refs: yup
    .mixed<Record<string, { row_config_ref: string; deck_config_ref: string }>>()
    .test(
      'refs',
      'Invalid private configuration references',
      (value) =>
        value === undefined ||
        (value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          Object.values(value).every(
            (entry) =>
              entry &&
              typeof entry.row_config_ref === 'string' &&
              Boolean(entry.row_config_ref) &&
              typeof entry.deck_config_ref === 'string' &&
              Boolean(entry.deck_config_ref)
          ))
    )
});
const privateResponseSchema = yup.object({
  report_id: yup.number().integer().positive().required(),
  deck_type: yup.string().required(),
  config_revision: yup.number().integer().positive().required(),
  config: privateConfigSchema.nullable().defined(),
  decks_with_private_config: yup.array().of(yup.string().required()).defined(),
  has_private_config: yup.boolean().defined()
});
export const parseReportPrivateConfig = (value: unknown): Promise<ReportPrivateConfigResponse> =>
  privateResponseSchema.validate(value, { strict: true });
